// FlightCard — WebFlightRow from web-screens.jsx.
// Renders one SSE-supplied offer or a skeleton when searching.

import { notYetWiredToast } from './primitives';
import { useIsMobile } from '@/hooks/useIsMobile';

export interface FlightOffer {
  id: string;
  airline: string;
  code: string;
  logoColor: string;
  from: string;
  to: string;
  depart: string;
  arrive: string;
  duration: string;
  stopsLabel: string;
  price: number;
  oldPrice?: number;
  deal?: string;
  co2Label?: string;
  seats?: number;
  score?: number;
}

interface FlightCardProps {
  offer: FlightOffer;
}

export function FlightCard({ offer: f }: FlightCardProps) {
  const isMobile = useIsMobile();
  const T = {
    surface:  '#131C30',
    ink:      '#F4F3F0',
    inkDim:   '#8C98B0',
    primary:  '#E8441A',
    yellow:   '#F5A623',
    accent:   '#4B8EDB',
    line:     'rgba(255,255,255,0.07)',
    lineHi:   'rgba(255,255,255,0.14)',
    bg:       '#0A0F1C',
  };

  const dealColor =
    f.deal === 'CHEAPEST'   ? T.primary :
    f.deal === 'BEST VALUE' ? T.yellow  :
    f.deal === 'PREMIUM'    ? T.accent  : T.accent;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => notYetWiredToast('Booking')}
      onKeyDown={(e) => e.key === 'Enter' && notYetWiredToast('Booking')}
      style={{
        background: T.surface,
        borderRadius: 20,
        padding: 20,
        marginBottom: 10,
        border: `1px solid ${T.line}`,
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '180px 1fr 200px 160px',
        gap: isMobile ? 12 : 24,
        alignItems: 'center',
        transition: 'border-color .15s, transform .15s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = T.lineHi;
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = T.line;
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
      }}
    >
      {/* Airline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: f.logoColor,
          flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff',
          fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
          fontSize: 12, fontWeight: 700,
        }}>{f.code}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.25 }}>{f.airline}</div>
          {f.deal && (
            <div style={{
              fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
              fontSize: 9, fontWeight: 700, letterSpacing: 1, color: dealColor, marginTop: 6,
            }}>● {f.deal}</div>
          )}
        </div>
      </div>

      {/* Route */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 26, fontWeight: 700, color: T.ink, letterSpacing: -0.02 }}>{f.depart}</div>
          <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 11, color: T.inkDim }}>{f.from}</div>
        </div>
        <div style={{ flex: 1, position: 'relative' }}>
          <div style={{ height: 1, background: T.lineHi, position: 'relative' }}>
            <div style={{ position: 'absolute', left: 0, top: -3, width: 6, height: 6, borderRadius: 3, background: T.lineHi }}/>
            <div style={{ position: 'absolute', right: 0, top: -3, width: 6, height: 6, borderRadius: 3, background: T.lineHi }}/>
          </div>
          <div style={{ textAlign: 'center', fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 11, color: T.inkDim, marginTop: 6, fontWeight: 500 }}>
            {f.duration} · {f.stopsLabel}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 26, fontWeight: 700, color: T.ink, letterSpacing: -0.02 }}>{f.arrive}</div>
          <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 11, color: T.inkDim }}>{f.to}</div>
        </div>
      </div>

      {/* Meta */}
      <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 11, color: T.inkDim, lineHeight: 1.7 }}>
        {f.co2Label && <div>🌿 <span style={{ color: T.primary, fontWeight: 600 }}>{f.co2Label}</span></div>}
        {f.seats !== undefined && <div>{f.seats} seats left</div>}
        {f.score !== undefined && <div>{f.score}/100 · score</div>}
      </div>

      {/* Price */}
      <div style={{ textAlign: 'right' }}>
        {f.oldPrice && (
          <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 11, color: T.inkDim, textDecoration: 'line-through' }}>₹{f.oldPrice}</div>
        )}
        <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 28, fontWeight: 700, color: T.ink, letterSpacing: -0.02 }}>₹{f.price}</div>
        <button style={{
          marginTop: 8, padding: '8px 18px', borderRadius: 999,
          background: 'transparent', color: T.ink,
          border: `1px solid ${T.lineHi}`,
          fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
          transition: 'all .15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = '#E8441A';
          (e.currentTarget as HTMLButtonElement).style.color = '#0A0F1C';
          (e.currentTarget as HTMLButtonElement).style.borderColor = '#E8441A';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          (e.currentTarget as HTMLButtonElement).style.color = T.ink;
          (e.currentTarget as HTMLButtonElement).style.borderColor = T.lineHi;
        }}
        >
          Select →
        </button>
      </div>
    </div>
  );
}

// Skeleton version shown while SSE stream is in searching state
export function FlightCardSkeleton() {
  const isMobile = useIsMobile();
  return (
    <div style={{
      background: '#131C30',
      borderRadius: 20,
      padding: 20,
      marginBottom: 10,
      border: '1px solid rgba(255,255,255,0.07)',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '180px 1fr 200px 160px',
      gap: isMobile ? 12 : 24,
      alignItems: 'center',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div className="shimmer" style={{ width: 36, height: 36, borderRadius: 10 }}/>
        <div style={{ flex: 1 }}>
          <div className="shimmer" style={{ height: 14, borderRadius: 6, marginBottom: 6 }}/>
          <div className="shimmer" style={{ height: 8, width: 60, borderRadius: 4 }}/>
        </div>
      </div>
      <div>
        <div className="shimmer" style={{ height: 26, borderRadius: 6, marginBottom: 8 }}/>
        <div className="shimmer" style={{ height: 8, borderRadius: 4 }}/>
      </div>
      <div>
        <div className="shimmer" style={{ height: 12, borderRadius: 4, marginBottom: 6 }}/>
        <div className="shimmer" style={{ height: 12, borderRadius: 4, marginBottom: 6 }}/>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="shimmer" style={{ height: 28, borderRadius: 6, marginBottom: 8 }}/>
        <div className="shimmer" style={{ height: 32, borderRadius: 999, width: 90, marginLeft: 'auto' }}/>
      </div>
    </div>
  );
}
